import { Button, Card, Stack, Typography } from '@mui/material';
import { FormikValues } from 'formik';
import { get } from 'lodash';
import { ComponentType, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getFullName, getProfileImage } from 'src/common/utils/format-utils';
import { Timer } from 'src/components/timer';
import { AttestationStatus } from 'src/enums/application-status.enum';
import { useDialog } from 'src/hooks';
import { absentAttestation, startAttestation } from 'src/thunks/attestation-thunks';
import { AttestationAbsentDialog } from '../attestation-absent-dialog';
import { AttestationInterruptDialog } from '../attestation-interrupt-dialog';
import useStyles from './styles';

interface AttestationApplicantCardProps {
  application: Record<string, any>;
}
export const AttestationApplicantCard: ComponentType<AttestationApplicantCardProps> = ({
  application,
}) => {
  const classes = useStyles();
  const { user } = application;
  const fullname = getFullName(user);
  const profileImage = getProfileImage(user.passportPhoto);
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [openAbsentDialog, closeAbsentDialog] = useDialog(AttestationAbsentDialog);
  const [openInterruptDialog, closeInterruptDialog] = useDialog(AttestationInterruptDialog);

  const [absentStage, setAbsentStage] = useState<boolean | null>(null);
  const [language, setLanguage] = useState<'ru' | 'uz' | null>(null);

  const attestationStatus = get(application, 'attestationStatus');
  const isEmptyStatus = attestationStatus === null;
  const isAbsent = attestationStatus === AttestationStatus.ABSENT;
  const isStarted = attestationStatus === AttestationStatus.STARTED;
  const isStopped = attestationStatus === AttestationStatus.STOPPED;

  const handleStart = () => {
    dispatch(startAttestation(user.id, { language }));
  };

  const handleAbsent = () => {
    openAbsentDialog({
      size: 'small',
      onSubmit: (values: FormikValues) => {
        dispatch(absentAttestation(user.id, values.explanation));
        closeAbsentDialog();
      },
    });
  };

  const handleInterrupt = () => {
    openInterruptDialog({
      size: 'small',
      onSubmit: (values: FormikValues) => {
        dispatch(absentAttestation(user.id, values.explanation));
        closeInterruptDialog();
      },
    });
  };

  const absentButtons = (
    <>
      <Button color="success" variant="contained" fullWidth onClick={() => setAbsentStage(false)}>
        {t('attestation.attending', 'Присутствует')}
      </Button>
      <Button color="error" variant="outlined" fullWidth onClick={handleAbsent}>
        {t('attestation.absent', 'Отсутствует')}
      </Button>
    </>
  );

  // const languageButtons = (
  //   <>
  //     <Button color="secondary" variant="contained" fullWidth onClick={() => setLanguage('ru')}>
  //       {t('attestation.lang.ru', 'На русском')}
  //     </Button>
  //     <Button color="secondary" variant="contained" fullWidth onClick={() => setLanguage('uz')}>
  //       {t('attestation.lang.uz', 'На узбекском')}
  //     </Button>
  //   </>
  // );

  const startButton = (
    <Button color="primary" variant="contained" fullWidth onClick={handleStart}>
      {t('attestation.start', 'Запустить аттестацию')}
    </Button>
  );

  const isStartedView = (
    <>
      <Button color="primary" variant="contained" fullWidth>
        <Timer timeLeft={application.timeLeft} />
      </Button>
      <Button color="error" variant="outlined" fullWidth onClick={handleInterrupt}>
        {t('action.stop', 'Остановить')}
      </Button>
    </>
  );

  const isStoppedView = (
    <Typography variant="body2" color="warning.light">
      {t('attestation.stopped', 'Остановлено')}
    </Typography>
  );

  const isAbsentView = (
    <Typography variant="body2" color="warning.light">
      {t('attestation.absent', 'Отсутствует')}
    </Typography>
  );

  const actionButtons = () => {
    if (isEmptyStatus) {
      if (absentStage === null) return absentButtons;
      else if (!absentStage) return startButton;
    } else if (isStarted) {
      return isStartedView;
    } else if (isStopped) {
      return isStoppedView;
    } else if (isAbsent) {
      return isAbsentView;
    }
  };

  const results = () => {
    const { isPassed } = application;
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        columnGap={2}
        width="100%"
      >
        <Typography variant="body2" color={`${isPassed ? 'success' : 'warning'}.light`}>
          {isPassed
            ? t('attestation.passed', 'Аттестация пройдена')
            : t('attestation.failed', 'Аттестация не пройдена')}
        </Typography>

        <Typography variant="body2">
          {Number(application.score).toFixed(1)} {t('points', 'баллов')}
        </Typography>
      </Stack>
    );
  };

  return (
    <Card
      className={classes.root}
      component={Stack}
      direction="row"
      spacing={2}
      sx={{ p: 2, height: '100%' }}
    >
      <img
        src={profileImage}
        alt={fullname}
        width="122"
        height="122"
        style={{ objectFit: 'cover', borderRadius: 4 }}
      />
      <Stack className="info" flexGrow={1}>
        <Typography variant="subtitle2" mb={1}>
          {fullname}
        </Typography>
        <Typography variant="body2" color="grey.600" mb={1}>
          {[user.passportSeries, user.passportNumber].join('')}
        </Typography>

        <Stack direction="row" spacing={1} mt="auto">
          {attestationStatus === AttestationStatus.COMPLETED ? results() : actionButtons()}
        </Stack>
      </Stack>
    </Card>
  );
};
